/**
 * Plain-JS (non-React) equivalent of `useVisibilityAwareInterval`.
 *
 * Starts a repeating interval that pauses when the tab is hidden and resumes
 * (with an immediate callback call for refresh-on-return) when visible again.
 *
 * Returns a cleanup function — call it to stop the interval and remove the
 * visibilitychange listener.
 *
 * Use this in service modules that live outside React. For React components
 * and hooks, prefer `useVisibilityAwareInterval` instead.
 */
export const visibilityAwareInterval = (callback: () => void, ms: number): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return () => undefined
  }

  let timerId: number | null = null

  const start = () => {
    if (timerId !== null) return
    timerId = window.setInterval(callback, ms)
  }

  const stop = () => {
    if (timerId === null) return
    window.clearInterval(timerId)
    timerId = null
  }

  const onVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      stop()
    } else {
      callback() // refresh-on-return
      start()
    }
  }

  // Only start if the tab is already visible.
  if (document.visibilityState !== 'hidden') start()

  document.addEventListener('visibilitychange', onVisibilityChange)

  return () => {
    stop()
    document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
